import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bike, Car, Truck, Smartphone, Ticket, Clock, LifeBuoy, Mail, ShieldCheck } from "lucide-react";
import { api, IMAGES } from "../lib/api";
import { PageTitle, SoonBadge, btnPrimary } from "../components/ui";

export function Obogo() {
  const [rideDemo, setRideDemo] = useState(false);
  useEffect(() => { api.get("/ride-demo/status").then(({ data }) => setRideDemo(data.enabled)).catch(() => {}); }, []);
  const services = [
    { icon: Bike, t: "Moto-táxi", d: "Viagens rápidas de moto pela cidade." },
    { icon: Car, t: "Carro", d: "Transporte em carro para mais conforto." },
    { icon: Truck, t: "Entregas", d: "Entrega das suas compras BÔ Market com estafetas parceiros.", live: true },
  ];
  return (
    <div className="flex flex-col gap-5" data-testid="obogo-page">
      <div className="relative min-h-[280px] overflow-hidden rounded-3xl">
        <img src={IMAGES.rider} alt="OBÔGO" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
        <div className="relative flex max-w-xl flex-col gap-3 p-8">
          <span className="w-fit rounded-full bg-bo-yellow px-2.5 py-0.5 text-[11px] font-bold uppercase text-bo-ink" data-testid="obogo-hero-badge">Entregas ativas · Passageiros em preparação</span>
          <h1 className="text-4xl font-extrabold text-white sm:text-5xl">OBÔGO <span className="text-bo-yellow">Mobilidade e Entregas</span></h1>
          <p className="text-white/90">O seu transporte, à distância de um clique. As entregas das encomendas BÔ Market já funcionam. O transporte de passageiros está em preparação.</p>
          <Link to="/estafeta/candidatura" className="w-fit rounded-full bg-bo-yellow px-5 py-2.5 text-sm font-bold text-bo-ink" data-testid="become-courier-btn">Ser estafeta OBÔGO</Link>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        {services.map(({ icon: Icon, t, d, live }) => (
          <div key={t} className="bo-card flex flex-col gap-2 p-5" data-testid={`obogo-service-${t}`}>
            <Icon className="h-7 w-7 text-bo-green" />
            <p className="font-bold">{t}</p><p className="text-sm text-gray-600">{d}</p>
            {live ? <span className="mt-2 w-fit rounded-full bg-bo-green px-4 py-2 text-sm font-semibold text-white">Primeira fase ativa</span>
              : rideDemo ? <Link to="/mobilidade" className="mt-2 w-fit rounded-full bg-amber-400 px-4 py-2 text-sm font-bold text-bo-ink" data-testid={`obogo-ride-demo-link-${t}`}>Experimentar DEMO</Link>
              : <button disabled className="mt-2 w-fit rounded-full bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-400">Em preparação</button>}
          </div>
        ))}
      </div>
      <p className="bo-card flex items-center gap-2 p-4 text-sm text-gray-600"><ShieldCheck className="h-4 w-4 text-bo-green" />Não existem viagens de passageiros nesta fase. As entregas são feitas apenas por estafetas aprovados pela administração.</p>
    </div>
  );
}

export function Recargas() {
  const ops = [{ name: "CST", color: "bg-sky-600" }, { name: "Unitel", color: "bg-orange-500" }];
  return (
    <div data-testid="topup-page">
      <PageTitle title="Recargas Digitais" sub="Recargas de telemóvel CST e Unitel. Serviço em preparação: nenhuma transação é processada." />
      <div className="grid gap-4 sm:grid-cols-2">
        {ops.map((o) => (
          <div key={o.name} className="bo-card flex flex-col gap-4 p-6" data-testid={`topup-card-${o.name.toLowerCase()}`}>
            <div className="flex items-center justify-between">
              <span className={`grid h-14 w-14 place-items-center rounded-2xl text-lg font-extrabold text-white ${o.color}`}>{o.name}</span>
              <SoonBadge />
            </div>
            <p className="font-bold">Recarga {o.name}</p>
            <input disabled placeholder="Número de telemóvel" className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm" />
            <button disabled className="rounded-full bg-gray-100 py-2.5 text-sm font-semibold text-gray-400" data-testid={`topup-btn-${o.name.toLowerCase()}`}>Disponível em breve</button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Soon({ title, icon: Icon = Clock, text }) {
  return (
    <div className="bo-card flex flex-col items-center gap-3 px-6 py-16 text-center" data-testid="coming-soon-page">
      <Icon className="h-10 w-10 text-bo-green" />
      <h1 className="text-2xl font-extrabold">{title}</h1>
      <SoonBadge />
      <p className="max-w-md text-sm text-gray-500">{text}</p>
      <Link to="/obogo" className={btnPrimary}>Saber mais sobre o OBÔGO</Link>
    </div>
  );
}

export const Transportes = () => <Soon title="Meus Transportes" icon={Car} text="Aqui verá o histórico das suas viagens OBÔGO quando o serviço estiver disponível." />;
export const Passes = () => <Soon title="Meus Passes" icon={Ticket} text="Os passes de mobilidade OBÔGO estão em preparação." />;

const FAQ = [
  ["Como funciona a comparação de preços?", "Cada produto pode ser vendido por várias lojas. Mostramos o preço, o stock, o custo de entrega e o total de cada loja para escolher a melhor oferta."],
  ["Como pago por transferência?", "Após confirmar a encomenda, recebe os dados bancários ativos. Envie o comprovativo na página da encomenda. A administração confirma o recebimento antes da preparação."],
  ["Posso pagar na entrega?", "Sim, quando a opção estiver disponível no checkout."],
  ["O que significa DEMO?", "Produtos, lojas e preços marcados DEMO são demonstrativos e servem apenas para testar a plataforma."],
  ["Como vendo no BÔ Market?", "Crie uma conta, confirme o email e candidate a sua loja em \"Vender no BÔ Market\". A administração aprova antes de poder vender."],
];

export function Help() {
  return (
    <div data-testid="help-page">
      <PageTitle title="Ajuda e Suporte" right={<Link to="/conta/reclamacoes" className="rounded-full bg-bo-green px-4 py-2 text-sm font-bold text-white" data-testid="help-complaint-link">Apresentar reclamação</Link>} />
      <div className="flex flex-col gap-3">
        {FAQ.map(([q, a]) => (
          <details key={q} className="bo-card group p-5" data-testid="faq-item">
            <summary className="cursor-pointer list-none font-semibold group-open:text-bo-green">{q}</summary>
            <p className="mt-2 text-sm text-gray-600">{a}</p>
          </details>
        ))}
        <div className="bo-card flex items-center gap-3 p-5 text-sm"><LifeBuoy className="h-5 w-5 text-bo-green" />
          <span className="flex-1">Precisa de mais ajuda? Consulte a sua encomenda em Meus Pedidos ou contacte a equipa BSGOTECH.</span>
          <Smartphone className="h-4 w-4 text-gray-400" /><Mail className="h-4 w-4 text-gray-400" />
        </div>
      </div>
    </div>
  );
}
